// Generated by GitHub Copilot
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Toast, ToastContainer } from "react-bootstrap";

const AuthErrorContext = createContext(null);

/**
 * Context provider for handling authentication errors globally
 * Displays a toast notification when a 401 error occurs and redirects to login
 */
export const AuthErrorProvider = ({ children }) => {
  const [showAuthErrorToast, setShowAuthErrorToast] = useState(false);
  const isRedirectingRef = useRef(false);

  /**
   * Handle 401 authentication errors
   * Shows a toast notification and redirects to login after a brief delay
   */
  const handleAuthError = useCallback(() => {
    // Prevent multiple simultaneous redirects (ref for synchronous guard)
    if (isRedirectingRef.current) {
      return;
    }

    isRedirectingRef.current = true;
    setShowAuthErrorToast(true);

    // Save current location for post-login redirect
    window.localStorage.setItem("destination", window.location.href);

    // Redirect to login after brief toast display
    setTimeout(() => {
      window.location.href = "/signin/login";
    }, 500);
  }, []);

  // Listen for auth error events from authFetch
  useEffect(() => {
    const handleAuthErrorEvent = () => {
      handleAuthError();
    };

    window.addEventListener("auth-error-401", handleAuthErrorEvent);

    return () => {
      window.removeEventListener("auth-error-401", handleAuthErrorEvent);
    };
  }, [handleAuthError]);

  return (
    <AuthErrorContext.Provider value={{ handleAuthError }}>
      {children}

      {/* Global authentication error toast */}
      <ToastContainer
        className="position-fixed p-3"
        position="top-center"
        style={{ zIndex: 9999 }}
      >
        <Toast
          show={showAuthErrorToast}
          onClose={() => setShowAuthErrorToast(false)}
          delay={500}
          autohide={false}
          bg="danger"
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">Session Expired</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Your session has expired. Redirecting to login page...
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </AuthErrorContext.Provider>
  );
};

/**
 * Hook to access authentication error handling
 * @returns {Object} Object containing handleAuthError function
 */
export const useAuthError = () => {
  const context = useContext(AuthErrorContext);
  if (!context) {
    throw new Error("useAuthError must be used within an AuthErrorProvider");
  }
  return context;
};
